import type { DocumentManager, TabDocument } from "../doc/DocumentManager.js";
import { TAB_COLOR_IDS, type TabColorId } from "../doc/tabColors.js";
import {
  getLastNullapadShareRecipientSecret,
  saveLastNullapadShareRecipientSecret,
} from "../io/nullapadShare.js";

function anchorContextMenuInViewport(menu: HTMLElement, anchorX: number, anchorY: number): void {
  const w = menu.offsetWidth;
  const h = menu.offsetHeight;
  const vpW = window.innerWidth;
  const vpH = window.innerHeight;
  let x = Math.max(0, anchorX);
  if (x + w > vpW) x = Math.max(0, vpW - w);
  let y = anchorY;
  if (y + h > vpH) {
    const yAbove = anchorY - h;
    y = yAbove >= 0 ? yAbove : Math.max(0, vpH - h);
  }
  menu.style.left = `${x}px`;
  menu.style.top = `${y}px`;
}

/** Multi-document tab strip (PROJECT §9 TabBar / §16.4). */
export class TabBar {
  readonly element: HTMLElement;
  /** Wraps `element` for overflow edge hints and layout (`min-width: 0`). */
  private readonly tabbarScrollClip: HTMLDivElement;
  private readonly contextMenu: HTMLDivElement;
  private contextTabIndex: number | null = null;
  private readonly onDocumentPointerDown: (e: PointerEvent) => void;
  private readonly onDocumentKeyDown: (e: KeyboardEvent) => void;

  private readonly renameTabBtn: HTMLButtonElement;
  private readonly renameBlock: HTMLDivElement;
  private readonly renameInput: HTMLInputElement;
  private readonly duplicateTabBtn: HTMLButtonElement;
  private readonly diffPickLeftBtn: HTMLButtonElement;
  private readonly diffPickRightBtn: HTMLButtonElement;
  private readonly diffLayoutSep: HTMLDivElement;
  private readonly diffMonacoLayoutBtn: HTMLButtonElement;
  private readonly diffSplitPanesBtn: HTMLButtonElement;
  private readonly tabReorderSep: HTMLDivElement;
  private readonly moveTabLeftBtn: HTMLButtonElement;
  private readonly moveTabRightBtn: HTMLButtonElement;
  private readonly dropIndicator: HTMLDivElement;
  private readonly titleToolsWrap: HTMLElement;
  private readonly colorFilterStrip: HTMLElement;
  private readonly mergeSeparatorInput: HTMLInputElement;
  private readonly mergeBtn: HTMLButtonElement;
  private readonly splitBtn: HTMLButtonElement;
  private readonly mergeToolsMenu: HTMLDivElement;
  private readonly mergeToolsToggleBtn: HTMLButtonElement;
  private readonly clearAllTabColorsBtn: HTMLButtonElement;
  /** Tab context menu: recipient secret (password) + Send; null if sharing disabled. */
  private readonly shareContextBlock: HTMLDivElement | null;
  private readonly shareRecipientInput: HTMLInputElement | null;
  private readonly shareSendBtn: HTMLButtonElement | null;
  private readonly spellcheckToggleBtn: HTMLButtonElement | null;
  private readonly getNativeSpellcheckEnabled?: () => boolean;
  private readonly onToggleNativeSpellcheck?: () => void;
  /** When set, tabs whose `tabColor` differs are hidden. */
  private colorFilter: TabColorId | null = null;
  private tabDragCleanup: (() => void) | null = null;
  private dragSourceTab: HTMLElement | null = null;
  private tabbarScrollObserver: ResizeObserver | null = null;
  private lastScrollportActiveIndex = -1;
  private readonly onTabbarScrollOrResize = (): void => {
    this.syncTabbarScrollHints();
  };

  constructor(
    private readonly dm: DocumentManager,
    host: HTMLElement,
    /** Where the tab color filter strip is mounted (e.g. title bar next to app caption). */
    colorFilterHost: HTMLElement,
    private readonly onSelect: (index: number, event?: MouseEvent) => void,
    private readonly onClose: (index: number) => void,
    private readonly onCloseOthers: (index: number) => void,
    private readonly onCloseAll: () => void,
    private readonly onCopyPathOrTitle: (index: number) => void,
    /** Double-click tab strip → new untitled tab. */
    private readonly onNewTab: () => void,
    /** Double-click a tab (not ×) → cycle group color. */
    private readonly onCycleTabColor: (index: number) => void,
    /** Set exact tab group color from context menu (`null` clears). */
    private readonly onSetTabColor: (index: number, color: TabColorId | null) => void,
    private readonly onDiffPickLeft: (index: number) => void,
    private readonly onDiffPickRight: (index: number) => void,
    private readonly onDiffTabMonacoLayout: (index: number) => void,
    private readonly onDiffTabSplitPanesLayout: (index: number) => void,
    private readonly onTabReorder: (fromIndex: number, insertIndex: number) => void,
    /** Merge all text tabs with the current filter color using `separator` (no color on new tab). */
    private readonly onMergeSameColor: (separator: string, color: TabColorId | null) => void,
    /** Split active text tab on newline+separator+newline; new tabs get numbered titles. */
    private readonly onSplitActiveBySeparator: (separator: string) => void,
    /** Title-bar swatch selection: new empty tabs and split parts use this color when non-null. */
    private readonly onNewTabGroupColorFromFilter: (color: TabColorId | null) => void,
    /** Clone the tab at the given index. */
    private readonly onDuplicateTab: (index: number) => void,
    getNativeSpellcheckEnabled?: () => boolean,
    onToggleNativeSpellcheck?: () => void,
    /** Tab context menu: share this tab’s buffer to another NullaPad (recipient 4–48 hex secret). */
    private readonly onShareTabWithSecret?: (tabIndex: number, recipientId: string) => void | Promise<void>,
    /** If set, mounted after Merge / Split in the title bar (same row when space allows). */
    titleBarToolbarSlot?: HTMLElement,
    private readonly isMultiviewActive?: () => boolean,
    private readonly getMultiviewExtraTabIndices?: () => number[],
    /** Runs after the tab strip is rebuilt (titles / multiview headers can sync here). */
    private readonly onAfterRefresh?: () => void,
    /** Rename the tab at the given index to a new display title. */
    private readonly onRenameTab?: (index: number, newTitle: string) => void,
  ) {
    this.getNativeSpellcheckEnabled = getNativeSpellcheckEnabled;
    this.onToggleNativeSpellcheck = onToggleNativeSpellcheck;
    this.element = document.createElement("div");
    this.element.className = "npp-tabbar";
    this.element.setAttribute("role", "tablist");
    this.contextMenu = document.createElement("div");
    this.contextMenu.className = "npp-tab-context";
    this.contextMenu.hidden = true;
    this.contextMenu.setAttribute("role", "menu");
    this.contextMenu.append(
      this.makeMenuItem("Close", () => {
        if (this.contextTabIndex === null) return;
        this.onClose(this.contextTabIndex);
        this.hideContextMenu();
      }),
      this.makeMenuItem("Close Others", () => {
        if (this.contextTabIndex === null) return;
        this.onCloseOthers(this.contextTabIndex);
        this.hideContextMenu();
      }),
      this.makeMenuItem("Close All", () => {
        this.onCloseAll();
        this.hideContextMenu();
      }),
      this.makeMenuItem("Copy Path / Title", () => {
        if (this.contextTabIndex === null) return;
        this.onCopyPathOrTitle(this.contextTabIndex);
        this.hideContextMenu();
      }),
    );
    // Rename Tab — inline input row in the context menu.
    this.renameTabBtn = this.makeMenuItem("Rename Tab…", () => {
      if (this.contextTabIndex === null) return;
      const doc = this.dm.documents[this.contextTabIndex];
      if (!doc) return;
      this.renameInput.value = doc.title;
      this.renameBlock.hidden = false;
      this.renameTabBtn.hidden = true;
      requestAnimationFrame(() => {
        this.renameInput.select();
        this.renameInput.focus();
      });
    });
    this.contextMenu.appendChild(this.renameTabBtn);
    const renameLabel = document.createElement("span");
    renameLabel.className = "npp-tab-context-share-label";
    renameLabel.textContent = "Rename tab";
    const renameRow = document.createElement("div");
    renameRow.className = "npp-tab-context-share-row";
    this.renameInput = document.createElement("input");
    this.renameInput.type = "text";
    this.renameInput.className = "npp-tab-context-rename-input";
    this.renameInput.setAttribute("aria-label", "New tab name");
    this.renameInput.spellcheck = false;
    this.renameInput.maxLength = 200;
    this.renameInput.addEventListener("pointerdown", (e) => e.stopPropagation());
    const renameOkBtn = document.createElement("button");
    renameOkBtn.type = "button";
    renameOkBtn.className = "npp-tab-context-share-send";
    renameOkBtn.textContent = "OK";
    renameOkBtn.addEventListener("pointerdown", (e) => e.stopPropagation());
    const commitRename = (): void => {
      const idx = this.contextTabIndex;
      if (idx === null) return;
      const newTitle = this.renameInput.value.trim();
      if (newTitle) this.onRenameTab?.(idx, newTitle);
      this.hideContextMenu();
    };
    renameOkBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      commitRename();
    });
    this.renameInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); commitRename(); }
      if (e.key === "Escape") { e.preventDefault(); this.hideContextMenu(); }
    });
    renameRow.append(this.renameInput, renameOkBtn);
    this.renameBlock = document.createElement("div");
    this.renameBlock.className = "npp-tab-context-rename";
    this.renameBlock.hidden = true;
    this.renameBlock.append(renameLabel, renameRow);
    this.contextMenu.appendChild(this.renameBlock);
    this.duplicateTabBtn = this.makeMenuItem("Duplicate Tab", () => {
      if (this.contextTabIndex === null || this.duplicateTabBtn.disabled) return;
      this.onDuplicateTab(this.contextTabIndex);
      this.hideContextMenu();
    });
    this.contextMenu.appendChild(this.duplicateTabBtn);
    if (getNativeSpellcheckEnabled && onToggleNativeSpellcheck) {
      const spellSep = document.createElement("div");
      spellSep.className = "npp-tab-context-sep";
      spellSep.setAttribute("role", "separator");
      const spellBtn = document.createElement("button");
      spellBtn.type = "button";
      spellBtn.className = "npp-tab-context-item";
      spellBtn.setAttribute("role", "menuitem");
      spellBtn.addEventListener("pointerdown", (e) => e.stopPropagation());
      spellBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        onToggleNativeSpellcheck();
        this.hideContextMenu();
      });
      this.contextMenu.append(spellSep, spellBtn);
      this.spellcheckToggleBtn = spellBtn;
    } else {
      this.spellcheckToggleBtn = null;
    }
    if (onShareTabWithSecret) {
      const shareBlock = document.createElement("div");
      shareBlock.className = "npp-tab-context-share";
      const lab = document.createElement("span");
      lab.className = "npp-tab-context-share-label";
      lab.textContent = "Share with";
      const row = document.createElement("div");
      row.className = "npp-tab-context-share-row";
      const inp = document.createElement("input");
      inp.type = "password";
      inp.className = "npp-tab-context-share-secret";
      inp.autocomplete = "off";
      inp.spellcheck = false;
      inp.placeholder = "Recipient secret";
      inp.maxLength = 48;
      inp.setAttribute("aria-label", "Recipient NullaPad secret");
      inp.addEventListener("pointerdown", (e) => e.stopPropagation());
      const send = document.createElement("button");
      send.type = "button";
      send.className = "npp-tab-context-share-send";
      send.textContent = "Send";
      send.setAttribute("aria-label", "Send tab to recipient NullaPad");
      send.addEventListener("pointerdown", (e) => e.stopPropagation());
      inp.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          send.click();
        }
      });
      const eye = document.createElement("button");
      eye.type = "button";
      eye.className = "npp-tab-context-share-eye";
      eye.setAttribute("aria-label", "Show or hide recipient secret");
      eye.setAttribute("aria-pressed", "false");
      eye.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zm0 12.5c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/></svg>';
      eye.addEventListener("pointerdown", (e) => e.stopPropagation());
      eye.addEventListener("click", (e) => {
        e.stopPropagation();
        const show = inp.type === "password";
        inp.type = show ? "text" : "password";
        eye.setAttribute("aria-pressed", show ? "true" : "false");
      });
      send.addEventListener("click", (e) => {
        e.stopPropagation();
        const idx = this.contextTabIndex;
        if (idx === null) return;
        const rid = inp.value.trim();
        if (!rid) return;
        void (async () => {
          try {
            await onShareTabWithSecret(idx, rid);
            saveLastNullapadShareRecipientSecret(rid);
            inp.value = getLastNullapadShareRecipientSecret();
            inp.type = "password";
            eye.setAttribute("aria-pressed", "false");
            this.hideContextMenu();
          } catch (err) {
            void alert((err as Error).message || String(err));
          }
        })();
      });
      row.append(inp, eye, send);
      shareBlock.append(lab, row);
      this.contextMenu.appendChild(shareBlock);
      this.shareContextBlock = shareBlock;
      this.shareRecipientInput = inp;
      this.shareSendBtn = send;
    } else {
      this.shareContextBlock = null;
      this.shareRecipientInput = null;
      this.shareSendBtn = null;
    }
    this.tabReorderSep = document.createElement("div");
    this.tabReorderSep.className = "npp-tab-context-sep";
    this.tabReorderSep.setAttribute("role", "separator");
    this.moveTabLeftBtn = this.makeMenuItem("Move tab left", () => {
      if (this.contextTabIndex === null || this.moveTabLeftBtn.disabled) return;
      const i = this.contextTabIndex;
      if (i > 0) this.onTabReorder(i, i - 1);
      this.hideContextMenu();
    });
    this.moveTabRightBtn = this.makeMenuItem("Move tab right", () => {
      if (this.contextTabIndex === null || this.moveTabRightBtn.disabled) return;
      const i = this.contextTabIndex;
      const n = this.dm.documents.length;
      if (i < n - 1) this.onTabReorder(i, i + 2);
      this.hideContextMenu();
    });
    this.contextMenu.append(this.tabReorderSep, this.moveTabLeftBtn, this.moveTabRightBtn);
    const diffSep = document.createElement("div");
    diffSep.className = "npp-tab-context-sep";
    diffSep.setAttribute("role", "separator");
    this.diffPickLeftBtn = this.makeMenuItem("Select left for diff", () => {
      if (this.contextTabIndex === null || this.diffPickLeftBtn.disabled) return;
      this.onDiffPickLeft(this.contextTabIndex);
      this.hideContextMenu();
    });
    this.diffPickRightBtn = this.makeMenuItem("Select right for diff", () => {
      if (this.contextTabIndex === null || this.diffPickRightBtn.disabled) return;
      this.onDiffPickRight(this.contextTabIndex);
      this.hideContextMenu();
    });
    this.diffLayoutSep = document.createElement("div");
    this.diffLayoutSep.className = "npp-tab-context-sep";
    this.diffLayoutSep.setAttribute("role", "separator");
    this.diffMonacoLayoutBtn = this.makeMenuItem("Monaco diff editor (linked panes)", () => {
      if (this.contextTabIndex === null || this.diffMonacoLayoutBtn.disabled) return;
      this.onDiffTabMonacoLayout(this.contextTabIndex);
      this.hideContextMenu();
    });
    this.diffSplitPanesBtn = this.makeMenuItem("Split view: two panes (full files, highlighted)", () => {
      if (this.contextTabIndex === null || this.diffSplitPanesBtn.disabled) return;
      this.onDiffTabSplitPanesLayout(this.contextTabIndex);
      this.hideContextMenu();
    });
    this.contextMenu.append(
      diffSep,
      this.diffPickLeftBtn,
      this.diffPickRightBtn,
      this.diffLayoutSep,
      this.diffMonacoLayoutBtn,
      this.diffSplitPanesBtn,
    );
    const colorSep = document.createElement("div");
    colorSep.className = "npp-tab-context-sep";
    colorSep.setAttribute("role", "separator");
    this.contextMenu.appendChild(colorSep);
    const clearColorBtn = this.makeMenuItem("Tab color: none", () => {
      if (this.contextTabIndex === null) return;
      this.onSetTabColor(this.contextTabIndex, null);
      this.hideContextMenu();
    });
    clearColorBtn.classList.add("npp-tab-context-item--color");
    clearColorBtn.dataset.tabColor = "none";
    this.contextMenu.appendChild(clearColorBtn);
    for (const id of TAB_COLOR_IDS) {
      const btn = this.makeMenuItem(`Tab color: ${id}`, () => {
        if (this.contextTabIndex === null) return;
        this.onSetTabColor(this.contextTabIndex, id);
        this.hideContextMenu();
      });
      btn.classList.add("npp-tab-context-item--color");
      btn.dataset.tabColor = id;
      this.contextMenu.appendChild(btn);
    }
    this.titleToolsWrap = document.createElement("div");
    this.titleToolsWrap.className = "npp-titlebar-tools";
    const { strip, clearAllTabColorsBtn } = this.buildColorFilterStrip();
    this.colorFilterStrip = strip;
    this.clearAllTabColorsBtn = clearAllTabColorsBtn;
    this.colorFilterStrip.classList.add("npp-tab-color-filters--in-titlebar");
    this.titleToolsWrap.appendChild(this.colorFilterStrip);

    this.mergeToolsMenu = document.createElement("div");
    this.mergeToolsMenu.className = "npp-titlebar-merge-menu";
    this.mergeToolsMenu.dataset.open = "false";
    this.mergeToolsToggleBtn = document.createElement("button");
    this.mergeToolsToggleBtn.type = "button";
    this.mergeToolsToggleBtn.className = "npp-titlebar-merge-more";
    this.mergeToolsToggleBtn.textContent = "...";
    this.mergeToolsToggleBtn.setAttribute("aria-label", "More tab tools");
    this.mergeToolsToggleBtn.setAttribute("aria-expanded", "false");
    this.mergeToolsToggleBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      const open = this.mergeToolsMenu.dataset.open === "true";
      this.mergeToolsMenu.dataset.open = open ? "false" : "true";
      this.mergeToolsToggleBtn.setAttribute("aria-expanded", open ? "false" : "true");
    });
    this.mergeToolsMenu.appendChild(this.mergeToolsToggleBtn);
    const mergeRow = document.createElement("div");
    mergeRow.className = "npp-titlebar-merge-row";
    const sepLabel = document.createElement("label");
    sepLabel.className = "npp-titlebar-merge-label";
    sepLabel.textContent = "Separator";
    this.mergeSeparatorInput = document.createElement("input");
    this.mergeSeparatorInput.type = "text";
    this.mergeSeparatorInput.className = "npp-titlebar-merge-sep";
    this.mergeSeparatorInput.id = "npp-merge-separator";
    this.mergeSeparatorInput.spellcheck = false;
    this.mergeSeparatorInput.value = "---SECTION---";
    this.mergeSeparatorInput.title =
      "Merge: inserted between files as \\n + marker + \\n. Split: cuts on the same pattern. Use \\n / \\t in the marker as needed.";
    sepLabel.htmlFor = this.mergeSeparatorInput.id;
    this.mergeBtn = document.createElement("button");
    this.mergeBtn.type = "button";
    this.mergeBtn.className = "npp-titlebar-merge-btn";
    this.mergeBtn.textContent = "Merge";
    this.mergeBtn.addEventListener("click", () => {
      let sep = this.mergeSeparatorInput.value;
      sep = sep.replace(/\\n/g, "\n").replace(/\\t/g, "\t");
      this.onMergeSameColor(sep, this.colorFilter);
      this.mergeToolsMenu.dataset.open = "false";
      this.mergeToolsToggleBtn.setAttribute("aria-expanded", "false");
    });
    this.splitBtn = document.createElement("button");
    this.splitBtn.type = "button";
    this.splitBtn.className = "npp-titlebar-merge-btn";
    this.splitBtn.textContent = "Split";
    this.splitBtn.addEventListener("click", () => {
      let sep = this.mergeSeparatorInput.value;
      sep = sep.replace(/\\n/g, "\n").replace(/\\t/g, "\t");
      this.onSplitActiveBySeparator(sep);
      this.mergeToolsMenu.dataset.open = "false";
      this.mergeToolsToggleBtn.setAttribute("aria-expanded", "false");
    });
    mergeRow.append(sepLabel, this.mergeSeparatorInput, this.mergeBtn, this.splitBtn);
    this.mergeToolsMenu.appendChild(mergeRow);
    this.titleToolsWrap.appendChild(this.mergeToolsMenu);
    if (titleBarToolbarSlot) {
      this.titleToolsWrap.appendChild(titleBarToolbarSlot);
    }
    colorFilterHost.appendChild(this.titleToolsWrap);

    this.tabbarScrollClip = document.createElement("div");
    this.tabbarScrollClip.className = "npp-tabbar-scroll-clip";
    this.tabbarScrollClip.appendChild(this.element);
    host.appendChild(this.tabbarScrollClip);
    this.element.addEventListener("scroll", this.onTabbarScrollOrResize, { passive: true });
    this.tabbarScrollObserver = new ResizeObserver(this.onTabbarScrollOrResize);
    this.tabbarScrollObserver.observe(this.element);
    host.appendChild(this.contextMenu);
    this.dropIndicator = document.createElement("div");
    this.dropIndicator.className = "npp-tabbar__drop-indicator";
    this.dropIndicator.setAttribute("aria-hidden", "true");
    this.dropIndicator.style.display = "none";
    host.appendChild(this.dropIndicator);

    this.onDocumentPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!this.contextMenu.hidden) {
        if (!(target && this.contextMenu.contains(target))) {
          this.hideContextMenu();
        }
      }
      if (this.mergeToolsMenu.dataset.open === "true") {
        if (!(target && this.mergeToolsMenu.contains(target))) {
          this.mergeToolsMenu.dataset.open = "false";
          this.mergeToolsToggleBtn.setAttribute("aria-expanded", "false");
        }
      }
    };
    this.onDocumentKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      let handled = false;
      if (!this.contextMenu.hidden) {
        this.hideContextMenu();
        handled = true;
      }
      if (this.mergeToolsMenu.dataset.open === "true") {
        this.mergeToolsMenu.dataset.open = "false";
        this.mergeToolsToggleBtn.setAttribute("aria-expanded", "false");
        handled = true;
      }
      if (handled) {
        e.preventDefault();
      }
    };
    document.addEventListener("pointerdown", this.onDocumentPointerDown, true);
    document.addEventListener("keydown", this.onDocumentKeyDown, true);
    /** New tab only on double-click empty strip — tab coloring uses `click` `detail === 2` (see `makeTab`). */
    this.element.addEventListener("dblclick", (e) => {
      const el = e.target as HTMLElement | null;
      if (!el) return;
      if (el.closest(".npp-tab-context")) return;
      if (el.closest(".npp-tab")) return;
      this.onNewTab();
    });
    this.refresh();
  }

  destroy(): void {
    this.cancelTabDrag();
    document.removeEventListener("pointerdown", this.onDocumentPointerDown, true);
    document.removeEventListener("keydown", this.onDocumentKeyDown, true);
    this.element.removeEventListener("scroll", this.onTabbarScrollOrResize);
    this.tabbarScrollObserver?.disconnect();
    this.tabbarScrollObserver = null;
    this.dropIndicator.remove();
    this.titleToolsWrap.remove();
    this.contextMenu.remove();
    this.tabbarScrollClip.remove();
  }

  private cancelTabDrag(): void {
    const run = this.tabDragCleanup;
    this.tabDragCleanup = null;
    this.element.classList.remove("npp-tabbar--dragging");
    const tab = this.dragSourceTab;
    if (tab) {
      tab.classList.remove("npp-tab--drag-source");
      tab.style.transform = "";
      tab.style.willChange = "";
    }
    this.dragSourceTab = null;
    this.dropIndicator.style.display = "none";
    run?.();
  }

  refresh(): void {
    this.element.replaceChildren();
    const docs = this.dm.documents;
    const extraTabIndices = this.getMultiviewExtraTabIndices?.() ?? [];
    const mv = this.isMultiviewActive?.() ?? false;
    for (let i = 0; i < docs.length; i++) {
      const tab = this.makeTab(
        docs[i]!,
        i,
        i === this.dm.activeIndex,
        mv && extraTabIndices.includes(i),
      );
      if (this.colorFilter !== null && docs[i]!.tabColor !== this.colorFilter) {
        tab.hidden = true;
      }
      this.element.appendChild(tab);
    }
    this.updateColorFilterButtons();
    this.reconcileColorFilterAfterTabs();
    const activeIdx = this.dm.activeIndex;
    const activeChanged = activeIdx !== this.lastScrollportActiveIndex;
    this.lastScrollportActiveIndex = activeIdx;
    requestAnimationFrame(() => {
      if (activeChanged) {
        this.ensureActiveTabInScrollport();
      }
      this.syncTabbarScrollHints();
    });
    this.onAfterRefresh?.();
  }

  /** When the active tab is clipped horizontally, scroll it into view without jumping if already visible. */
  private ensureActiveTabInScrollport(): void {
    const active = this.element.querySelector(".npp-tab-active");
    if (!(active instanceof HTMLElement)) return;
    const bar = this.element;
    const a = active.getBoundingClientRect();
    const b = bar.getBoundingClientRect();
    const pad = 6;
    if (a.left < b.left + pad || a.right > b.right - pad) {
      active.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "auto" });
    }
  }

  private syncTabbarScrollHints(): void {
    const bar = this.element;
    const clip = this.tabbarScrollClip;
    const maxScroll = bar.scrollWidth - bar.clientWidth;
    if (maxScroll <= 1) {
      clip.removeAttribute("data-edge-left");
      clip.removeAttribute("data-edge-right");
      return;
    }
    if (bar.scrollLeft > 2) {
      clip.setAttribute("data-edge-left", "");
    } else {
      clip.removeAttribute("data-edge-left");
    }
    if (bar.scrollLeft < maxScroll - 2) {
      clip.setAttribute("data-edge-right", "");
    } else {
      clip.removeAttribute("data-edge-right");
    }
  }

  getColorFilter(): TabColorId | null {
    return this.colorFilter;
  }

  /** Clear color filter and refresh tabs (same as “All”). */
  clearColorFilter(): void {
    if (this.colorFilter === null) return;
    this.colorFilter = null;
    this.updateColorFilterButtons();
    this.refresh();
  }

  private buildColorFilterStrip(): { strip: HTMLElement; clearAllTabColorsBtn: HTMLButtonElement } {
    const row = document.createElement("div");
    row.className = "npp-tab-color-filters";
    row.setAttribute("role", "toolbar");
    row.setAttribute("aria-label", "Filter tabs by group color");
    const all = document.createElement("button");
    all.type = "button";
    all.className = "npp-tab-color-filter npp-tab-color-filter--swatch npp-tab-color-filter--all";
    all.title = "Show all tabs";
    all.addEventListener("click", () => this.clearColorFilter());
    row.appendChild(all);
    for (const id of TAB_COLOR_IDS) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = `npp-tab-color-filter npp-tab-color-filter--swatch npp-tab-color-filter--${id}`;
      b.title = `Select this color: filter tabs, merge target, and color for new empty tabs / split parts. If no tab uses this color yet, a new empty tab is created. Click again to clear.`;
      b.dataset.filterColor = id;
      b.setAttribute("aria-label", `Filter tabs: ${id}`);
      b.addEventListener("click", () => {
        this.colorFilter = this.colorFilter === id ? null : id;
        this.applyColorFilterAfterChipClick();
      });
      row.appendChild(b);
    }
    const clearAllTabColorsBtn = document.createElement("button");
    clearAllTabColorsBtn.type = "button";
    clearAllTabColorsBtn.className = "npp-tab-color-filter npp-tab-color-filter--clear-tab-colors";
    clearAllTabColorsBtn.textContent = "×";
    clearAllTabColorsBtn.title =
      "Remove group color from every tab and clear the title-bar color selection (filter / new-tab color).";
    clearAllTabColorsBtn.setAttribute("aria-label", "Clear all tab group colors");
    clearAllTabColorsBtn.addEventListener("click", () => {
      this.dm.clearAllTabColors();
      this.colorFilter = null;
      this.updateColorFilterButtons();
      this.refresh();
    });
    row.appendChild(clearAllTabColorsBtn);
    return { strip: row, clearAllTabColorsBtn };
  }

  private applyColorFilterAfterChipClick(): void {
    this.updateColorFilterButtons();
    if (this.colorFilter === null) {
      this.refresh();
      return;
    }
    const docs = this.dm.documents;
    const activeDoc = docs[this.dm.activeIndex];
    if (activeDoc?.tabColor === this.colorFilter) {
      this.refresh();
      return;
    }
    const first = docs.findIndex((d) => d.tabColor === this.colorFilter);
    if (first < 0) {
      // `updateColorFilterButtons` already set the document manager's new-tab group color to this filter.
      this.onNewTab();
      return;
    }
    this.onSelect(first);
  }

  private updateColorFilterButtons(): void {
    const allBtn = this.colorFilterStrip.querySelector(".npp-tab-color-filter--all");
    if (allBtn instanceof HTMLButtonElement) {
      allBtn.setAttribute("aria-pressed", this.colorFilter === null ? "true" : "false");
    }
    for (const id of TAB_COLOR_IDS) {
      const b = this.colorFilterStrip.querySelector(`[data-filter-color="${id}"]`);
      if (b instanceof HTMLButtonElement) {
        const count = this.dm.documents.filter((d) => d.tabColor === id).length;
        b.disabled = false;
        b.dataset.emptySwatch = count === 0 ? "true" : "false";
        b.setAttribute("aria-pressed", this.colorFilter === id ? "true" : "false");
      }
    }
    this.updateMergeButtonState();
    this.updateSplitButtonState();
    this.clearAllTabColorsBtn.disabled = !this.dm.documents.some((d) => d.tabColor !== null);
    this.onNewTabGroupColorFromFilter(this.colorFilter);
  }

  private updateMergeButtonState(): void {
    const c = this.colorFilter;
    if (c === null) {
      this.mergeBtn.disabled = true;
      this.mergeBtn.title =
        "Choose a color swatch first. Merge combines text tabs of that color into one uncolored tab.";
      return;
    }
    const n = this.dm.documents.filter((d) => d.tabColor === c && d.viewMode === "text").length;
    this.mergeBtn.disabled = n < 2;
    this.mergeBtn.title =
      n < 2
        ? "Need at least two text tabs with the selected color."
        : `Merge ${n} text tabs into one tab (no color). Separator is taken from the field.`;
  }

  private updateSplitButtonState(): void {
    const d = this.dm.active;
    const ok = Boolean(d && d.viewMode === "text");
    this.splitBtn.disabled = !ok;
    this.splitBtn.title = ok
      ? this.colorFilter === null
        ? "Split the active text tab on newline+separator+newline. New tabs: base-1, -2, … Pick a title-bar swatch to color new tabs and split parts."
        : "Split the active text tab on newline+separator+newline. New tabs: base-1, -2, … use the selected swatch color."
      : "Activate a text tab to split.";
  }

  private reconcileColorFilterAfterTabs(): void {
    if (this.colorFilter === null) return;
    const docs = this.dm.documents;
    const matchCount = docs.filter((d) => d.tabColor === this.colorFilter).length;
    if (matchCount === 0) return;
    const activeDoc = docs[this.dm.activeIndex];
    if (activeDoc?.tabColor === this.colorFilter) return;
    const first = docs.findIndex((d) => d.tabColor === this.colorFilter);
    if (first >= 0) this.onSelect(first);
  }

  private makeTab(doc: TabDocument, index: number, primaryActive: boolean, multiviewExtraPane: boolean): HTMLElement {
    const tab = document.createElement("div");
    tab.className =
      "npp-tab" +
      (primaryActive ? " npp-tab-active" : "") +
      (multiviewExtraPane ? " npp-tab-split-secondary" : "");
    tab.setAttribute("role", "tab");
    tab.setAttribute("aria-selected", primaryActive ? "true" : "false");
    tab.dataset.tabIndex = String(index);
    tab.dataset.docId = doc.id;
    const baseTitle = doc.filePath ?? doc.title;
    tab.title = `${baseTitle} — Double-click: cycle group color`;
    if (doc.tabColor) {
      tab.classList.add(`npp-tab--group-${doc.tabColor}`);
      tab.dataset.tabColor = doc.tabColor;
    }
    if (doc.language === "max") {
      tab.classList.add("npp-tab--max-mode");
    }

    const icon = document.createElement("span");
    const iconKind = tabSaveStateIconClassFor(doc);
    icon.className = `npp-tab-icon ${iconKind}`;
    if (iconKind.includes("npp-tab-max-logotype")) {
      icon.textContent = "MAX";
      icon.setAttribute("aria-hidden", "true");
    }

    const label = document.createElement("span");
    label.className = "npp-tab-label";
    label.textContent = doc.title;

    const close = document.createElement("button");
    close.type = "button";
    close.className = "npp-tab-close";
    close.setAttribute("aria-label", "Close tab");
    close.textContent = "×";

    tab.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).closest(".npp-tab-close")) return;
      // Second click of a double-click: `dblclick` is unreliable if the first `click` refreshed
      // the tab strip (DOM node replaced). `detail === 2` still fires on the new node.
      if (e.detail === 2) {
        e.preventDefault();
        this.onCycleTabColor(index);
        return;
      }
      this.onSelect(index, e);
    });
    close.addEventListener("click", (e) => {
      e.stopPropagation();
      this.onClose(index);
    });
    tab.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      this.contextTabIndex = index;
      this.showContextMenu(e.clientX, e.clientY);
    });
    tab.addEventListener("pointerdown", (e) => {
      if (e.button !== 0) return;
      if ((e.target as HTMLElement).closest(".npp-tab-close")) return;
      this.beginTabPointerReorder(e, tab, index);
    });
    tab.append(icon, label, close);
    return tab;
  }


  /** Snapshot tab geometry by buffer id — call before `DocumentManager.moveTab`, then `refresh()`, then `animateReorderFrom`. */
  captureLayoutForReorder(): Map<string, DOMRect> {
    const map = new Map<string, DOMRect>();
    for (const el of this.element.querySelectorAll<HTMLElement>(".npp-tab")) {
      const id = el.dataset.docId;
      if (id) map.set(id, el.getBoundingClientRect());
    }
    return map;
  }

  /** FLIP: animate tabs from pre-`refresh()` rects to current layout. */
  animateReorderFrom(before: Map<string, DOMRect>): void {
    if (before.size === 0) return;
    try {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    } catch {
      /* ignore */
    }

    const tabs = [...this.element.querySelectorAll<HTMLElement>(".npp-tab")];
    const animating: HTMLElement[] = [];
    for (const tab of tabs) {
      const id = tab.dataset.docId;
      if (!id) continue;
      const prev = before.get(id);
      if (!prev) continue;
      const curr = tab.getBoundingClientRect();
      const dx = prev.left - curr.left;
      const dy = prev.top - curr.top;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) continue;
      tab.style.willChange = "transform";
      tab.style.transition = "none";
      tab.style.transform = `translate(${dx}px, ${dy}px)`;
      animating.push(tab);
    }
    if (animating.length === 0) return;

    void this.element.offsetHeight;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const ease = "cubic-bezier(0.25, 0.1, 0.25, 1)";
        for (const tab of animating) {
          tab.style.transition = `transform 240ms ${ease}`;
          tab.style.transform = "";
        }
        window.setTimeout(() => {
          for (const tab of animating) {
            tab.style.transition = "";
            tab.style.transform = "";
            tab.style.willChange = "";
          }
          this.syncTabbarScrollHints();
        }, 280);
      });
    });
  }

  /** Drop slot 0..n from horizontal pointer position (midpoint scan). */
  private computeInsertIndexFromPointer(clientX: number): number {
    const tabs = [...this.element.querySelectorAll<HTMLElement>(".npp-tab")];
    let insert = 0;
    for (let i = 0; i < tabs.length; i++) {
      const r = tabs[i]!.getBoundingClientRect();
      if (clientX >= r.left + r.width / 2) insert = i + 1;
    }
    return insert;
  }

  /** Vertical line in viewport showing the insertion slot under the pointer. */
  private positionDropIndicator(pointerClientX: number): void {
    const insert = this.computeInsertIndexFromPointer(pointerClientX);
    const tabs = [...this.element.querySelectorAll<HTMLElement>(".npp-tab")];
    const bar = this.element.getBoundingClientRect();
    let x: number;
    if (tabs.length === 0) {
      x = bar.left;
    } else if (insert <= 0) {
      x = tabs[0]!.getBoundingClientRect().left;
    } else if (insert >= tabs.length) {
      x = tabs[tabs.length - 1]!.getBoundingClientRect().right;
    } else {
      const a = tabs[insert - 1]!.getBoundingClientRect().right;
      const b = tabs[insert]!.getBoundingClientRect().left;
      x = (a + b) / 2;
    }
    this.dropIndicator.style.display = "block";
    this.dropIndicator.style.left = `${x - 1.5}px`;
    this.dropIndicator.style.top = `${bar.top + 3}px`;
    this.dropIndicator.style.height = `${Math.max(14, bar.height - 6)}px`;
  }

  private beginTabPointerReorder(e: PointerEvent, tabEl: HTMLElement, fromIndex: number): void {
    if (this.dm.documents.length <= 1) return;
    this.cancelTabDrag();
    this.dragSourceTab = tabEl;
    const pointerId = e.pointerId;
    const originX = e.clientX;
    const originY = e.clientY;
    let moved = false;
    let finishDone = false;
    let reduceMotion = false;
    try {
      reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      /* ignore */
    }
    try {
      tabEl.setPointerCapture(pointerId);
    } catch {
      /* ignore */
    }

    /** Arm drag after ~4px diagonal or ~3px mostly horizontal. */
    const thresholdSq = 16;
    const ac = new AbortController();
    const { signal } = ac;
    this.tabDragCleanup = () => {
      ac.abort();
    };

    const finish = (ev: PointerEvent): void => {
      if (ev.pointerId !== pointerId) return;
      if (finishDone) return;
      finishDone = true;
      try {
        tabEl.releasePointerCapture(pointerId);
      } catch {
        /* ignore */
      }
      const wasMoved = moved;
      const insertX = ev.clientX;
      const insertIndex = wasMoved ? this.computeInsertIndexFromPointer(insertX) : -1;
      this.cancelTabDrag();
      if (wasMoved) {
        this.onTabReorder(fromIndex, insertIndex);
      }
    };

    const updateDragFeedback = (clientX: number, clientY: number): void => {
      const dx = clientX - originX;
      const dyLift = reduceMotion
        ? 0
        : Math.max(-6, Math.min(6, (clientY - originY) * 0.22));
      const scale = reduceMotion ? "" : " scale(1.035)";
      tabEl.style.willChange = "transform";
      tabEl.style.transform = `translate3d(${dx}px, ${dyLift}px, 0)${scale}`;
      this.positionDropIndicator(clientX);
    };

    const onMove = (ev: PointerEvent): void => {
      if (ev.pointerId !== pointerId) return;
      const dx = ev.clientX - originX;
      const dy = ev.clientY - originY;
      const horizontal = Math.abs(dx) >= 3 && Math.abs(dx) > Math.abs(dy) * 0.65;
      if (!moved && (dx * dx + dy * dy >= thresholdSq || horizontal)) {
        moved = true;
        this.element.classList.add("npp-tabbar--dragging");
        this.dragSourceTab?.classList.add("npp-tab--drag-source");
        updateDragFeedback(ev.clientX, ev.clientY);
        return;
      }
      if (moved) {
        updateDragFeedback(ev.clientX, ev.clientY);
      }
    };

    document.addEventListener("pointermove", onMove, { signal, capture: true });
    document.addEventListener("pointerup", finish, { signal, capture: true });
    document.addEventListener("pointercancel", finish, { signal, capture: true });
  }

  private makeMenuItem(label: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "npp-tab-context-item";
    btn.setAttribute("role", "menuitem");
    btn.textContent = label;
    btn.addEventListener("click", onClick);
    return btn;
  }

  private showContextMenu(clientX: number, clientY: number): void {
    const doc =
      this.contextTabIndex !== null ? this.dm.documents[this.contextTabIndex] ?? null : null;
    const textOnly = doc?.viewMode === "text";
    const isDiff = doc?.viewMode === "diff";
    const canDuplicate =
      !!doc &&
      !doc.sessionRestorePlaceholder &&
      doc.viewMode !== "image" &&
      doc.viewMode !== "pdf";
    this.duplicateTabBtn.disabled = !canDuplicate;
    // Reset rename row to default (hidden) on each open.
    this.renameBlock.hidden = true;
    this.renameTabBtn.hidden = false;
    this.diffPickLeftBtn.disabled = !textOnly;
    this.diffPickRightBtn.disabled = !textOnly;
    this.diffLayoutSep.hidden = !isDiff;
    this.diffMonacoLayoutBtn.hidden = !isDiff;
    this.diffSplitPanesBtn.hidden = !isDiff;
    if (isDiff && doc) {
      this.diffMonacoLayoutBtn.disabled = !doc.diffSplitView;
      this.diffSplitPanesBtn.disabled = doc.diffSplitView;
    } else {
      this.diffMonacoLayoutBtn.disabled = true;
      this.diffSplitPanesBtn.disabled = true;
    }
    const n = this.dm.documents.length;
    const ci = this.contextTabIndex;
    this.moveTabLeftBtn.disabled = ci === null || ci <= 0 || n <= 1;
    this.moveTabRightBtn.disabled = ci === null || ci >= n - 1 || n <= 1;
    if (this.spellcheckToggleBtn) {
      const spellOn = this.getNativeSpellcheckEnabled?.() ?? true;
      this.spellcheckToggleBtn.textContent = spellOn ? "Disable spell checking" : "Enable spell checking";
    }
    const shareable = Boolean(doc && (doc.viewMode === "text" || doc.viewMode === "video"));
    if (this.shareContextBlock && this.shareRecipientInput && this.shareSendBtn) {
      this.shareContextBlock.hidden = !shareable;
      this.shareRecipientInput.disabled = !shareable;
      this.shareSendBtn.disabled = !shareable;
      if (shareable) {
        this.shareRecipientInput.value = getLastNullapadShareRecipientSecret();
        this.shareRecipientInput.type = "password";
        this.shareContextBlock.querySelector<HTMLButtonElement>(".npp-tab-context-share-eye")?.setAttribute(
          "aria-pressed",
          "false",
        );
      }
    }
    this.contextMenu.style.visibility = "hidden";
    this.contextMenu.hidden = false;
    anchorContextMenuInViewport(this.contextMenu, clientX, clientY);
    this.contextMenu.style.visibility = "";
  }

  private hideContextMenu(): void {
    this.contextMenu.hidden = true;
    this.contextTabIndex = null;
    this.renameBlock.hidden = true;
    this.renameTabBtn.hidden = false;
    if (this.shareRecipientInput) {
      saveLastNullapadShareRecipientSecret(this.shareRecipientInput.value);
      this.shareRecipientInput.value = getLastNullapadShareRecipientSecret();
      this.shareRecipientInput.type = "password";
    }
    this.shareContextBlock
      ?.querySelector<HTMLButtonElement>(".npp-tab-context-share-eye")
      ?.setAttribute("aria-pressed", "false");
  }

  syncSpellcheckToggleLabel(): void {
    if (!this.spellcheckToggleBtn) return;
    const spellOn = this.getNativeSpellcheckEnabled?.() ?? true;
    this.spellcheckToggleBtn.textContent = spellOn ? "Disable spell checking" : "Enable spell checking";
  }
}

function tabSaveStateIconClassFor(doc: TabDocument): string {
  if (doc.fromShare) {
    return doc.dirty ? "npp-ico-share-dirty" : "npp-ico-share-clean";
  }
  if (doc.viewMode === "video") {
    return doc.dirty ? "npp-ico-nulla-mic-dirty" : "npp-ico-nulla-mic";
  }
  const isUntitledNewTab =
    doc.viewMode === "text" &&
    doc.filePath === null &&
    doc.fileHandle === null &&
    doc.title.startsWith("Untitled-");
  if (doc.language === "max") {
    return doc.dirty || isUntitledNewTab
      ? "npp-tab-max-logotype npp-tab-max-logotype--dirty"
      : "npp-tab-max-logotype";
  }
  return doc.dirty || isUntitledNewTab ? "npp-ico-diskette-dirty" : "npp-ico-diskette-clean";
}
